/**
 * <SubscriptionPortalPage> — l'onglet « Abonnement » de l'espace parent (page, L0.1).
 *
 * Composition pure : la coque `<PageTemplate>` (chargement / erreur / réessayer), le corps
 * `<SubscriptionPortalTemplate>`, et la feuille « Date de prélèvement ». Aucune dérivation ici —
 * offre, statut, dates et conditions d'affichage arrivent du view-model, déjà formulés.
 *
 * Les quatre parcours que les gestes ouvrent (pause, heures supplémentaires, activation,
 * résiliation) restent legacy : la page ne fait que les appeler.
 *
 * `collectionDay` est le jeu de props complet de `<PaymentCollectionDayFlow>`, construit par
 * `usePaymentCollectionDayAdapter` (même patron que `mediaProps` de `useSendTutorMessageAdapter`) :
 * le geste est contrôlé, donc son étape et son jour choisi sont de l'état métier, et la page n'a
 * pas à les relayer prop par prop.
 */
import React from "react"

import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { PaymentCollectionDayFlow, type PaymentCollectionDayFlowProps } from "../../templates/PaymentCollectionDayFlow"
import {
    SubscriptionPortalTemplate,
    type SubscriptionPortalTemplateProps,
} from "../../templates/SubscriptionPortalTemplate/SubscriptionPortalTemplate"
import { SubscriptionPortalSkeleton } from "./SubscriptionPortalSkeleton"

export type SubscriptionPortalPageProps = SubscriptionPortalTemplateProps & {
    /** Premier chargement — remplace le corps, garde la coque. */
    loading?: boolean
    error?: unknown
    onRetry?: () => void

    /** Feuille « Date de prélèvement » — absente si le geste n'est pas offert. */
    collectionDay?: PaymentCollectionDayFlowProps | null
}

export function SubscriptionPortalPage(props: SubscriptionPortalPageProps) {
    const { loading = false, error, onRetry, collectionDay, testID, ...templateProps } = props

    return (
        <PageTemplate
            testID={testID}
            loading={loading}
            error={error}
            onRetry={onRetry}
            skeleton={<SubscriptionPortalSkeleton />}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            <SubscriptionPortalTemplate {...templateProps} />

            {collectionDay != null && <PaymentCollectionDayFlow {...collectionDay} />}
        </PageTemplate>
    )
}
